import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { RootState } from '../../../../types/common.types';
import { Poll, Post } from '../../../../types/entity.types';

import { usePollApi } from '../../../../api/poll/usePollApi';
import { useReactionApi } from '../../../../api/reaction/useReactionApi';

import { addReaction, removeReaction } from '../../../../slices/programme/tribe/tribe.slice';

import { generateUUID } from '../../../../utils/util';

import TribePoll from '../../../../components/screens/programme/tribe/tribe-poll/TribePoll';

type Props = {
  post: Post;
};

const TribePollContainer = ({ post }: Props) => {
  const [poll, setPoll] = useState<Poll | undefined>(undefined);

  const { reactions, pollResults } = useSelector(({ tribe }: RootState) => tribe);
  const { members } = useSelector(({ member }: RootState) => member);
  const { user } = useSelector(({ system }: RootState) => system);

  const dispatch = useDispatch();

  const { programmeExternalReference } = useParams();

  const { viewPollResults } = usePollApi();

  const {
    createReaction: createReactionRequest,
    deleteReaction: deleteReactionRequest,
    switchReaction: switchReactionRequest
  } = useReactionApi();

  useEffect(() => {
    if (!post.question || !post.options) return;

    const filteredReactions = reactions.filter((reaction) => reaction.postExternalReference === post.externalReference);

    const poll: Poll = {
      question: post.question as string,
      options: post.options.map((option) => {
        const votes = post.reactionCount?.find((reaction) => reaction.text === option)?.count || 0;
        const voted = filteredReactions.some((reaction) => reaction.reaction === option);

        return { id: generateUUID(), value: option, votes, voted };
      }),
      allowMultipleSelection: post.allowMultipleSelection,
      anonymous: post.anonymous
    };

    setPoll(poll);
  }, [post, reactions]);

  if (!poll) return <Fragment />;

  const postExternalReference = post.externalReference as string;

  const createReaction = (reaction: string) => {
    dispatch(addReaction({ postExternalReference, reaction }));

    createReactionRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        postExternalReference,
        reaction
      },
      undefined,
      () => {
        dispatch(removeReaction({ postExternalReference, reaction }));
      }
    );
  };

  const deleteReaction = (reaction: string) => {
    dispatch(removeReaction({ postExternalReference, reaction }));

    const member = members.find((member) => member.username === user?.username);

    if (!member) return;

    deleteReactionRequest(
      {
        memberExternalReference: member.memberExternalReference!,
        postExternalReference,
        reaction
      },
      undefined,
      () => {
        dispatch(addReaction({ postExternalReference, reaction }));
      }
    );
  };

  const switchReaction = (oldReaction: string, newReaction: string) => {
    const member = members.find((member) => member.username === user?.username);

    if (!member) return;

    switchReactionRequest({
      memberExternalReference: member.memberExternalReference!,
      programmeExternalReference: programmeExternalReference!,
      postExternalReference,
      oldReaction,
      newReaction
    });
  };

  const retrieveResults = (onSuccess: () => void) => {
    viewPollResults(
      {
        pollExternalReference: post.externalReference!,
        programmeExternalReference: programmeExternalReference!
      },
      onSuccess
    );
  };

  return (
    <TribePoll
      poll={poll}
      createReaction={createReaction}
      deleteReaction={deleteReaction}
      switchReaction={switchReaction}
      pollResults={pollResults}
      retrieveResults={retrieveResults}
      post
    />
  );
};

export default TribePollContainer;
